import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { setUser, setProducts, setPrices, getSubscriptions } from '../features/userSlice'
import { type IForgotPasswordQuery, type IUserProfile, type IProduct, type IPrice, type IIHFCredentials } from './types'
import { HEADERS_TITLE } from './constants'
import { STATISTICS, USER_END_POINT } from './endpoints'

const BASE_URL = process.env.REACT_APP_API_URL as string

export const userApi = createApi({
  reducerPath: 'userApi',
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      headers.set('Accept', 'application/json')
      return headers
    }
  }),
  endpoints: (builder) => ({
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    getUser: builder.mutation<IUserProfile, void>({
      query: () => {
        return {
          url: USER_END_POINT.USER,
          credentials: 'include',
          method: 'GET'
        }
      },
      transformResponse: (res: IUserProfile) => {
        return res
      },
      async onQueryStarted (args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(setUser(data))
        } catch (error) {
          // console.error(error)
        }
      }
    }),
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    getProducts: builder.mutation<IProduct, void>({
      query () {
        return {
          url: USER_END_POINT.STRIPE_PRODUCTS,
          method: 'GET',
          credentials: 'include'
        }
      },
      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
      transformResponse: (res: IProduct) => {
        return res
      },
      async onQueryStarted (args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(setProducts(data))
        } catch (error) {
          // console.error(error)
        }
      }
    }),
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    getPrices: builder.mutation<IPrice, void>({
      query () {
        return {
          url: USER_END_POINT.STRIPE_PRICES,
          method: 'GET',
          credentials: 'include'
        }
      },
      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
      transformResponse: (res: IPrice) => {
        return res
      },
      async onQueryStarted (args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(setPrices(data))
        } catch (error) {
          // console.error(error)
        }
      }
    }),
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    getSubscription: builder.mutation<void, void>({
      query () {
        return {
          url: USER_END_POINT.STRIPE_SUBSCRIPTION,
          method: 'GET',
          credentials: 'include'
        }
      },
      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
      transformResponse: (res: void) => {
        return res
      },
      async onQueryStarted (args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(getSubscriptions(data))
        } catch (error) {
          // console.error(error)
        }
      }
    }),
    postSubscription: builder.mutation({
      query: (credentials) => {
        const { productId, priceId, token } = credentials
        return {
          url: USER_END_POINT.STRIPE_SUBSCRIPTION,
          method: 'POST',
          body: { productId, priceId },
          credentials: 'include',
          headers: {
            [HEADERS_TITLE.XSRF]: token
          }
        }
      }
    }),
    deleteSubscription: builder.mutation({
      query: (credentials) => {
        const { priceId, reason, token } = credentials
        return {
          url: USER_END_POINT.STRIPE_SUBSCRIPTION,
          method: 'DELETE',
          body: { priceId, reason },
          credentials: 'include',
          headers: {
            [HEADERS_TITLE.XSRF]: token
          }
        }
      }
    }),
    patchSubscription: builder.mutation({
      query: (credentials) => {
        const { oldPriceId, newPriceId, token } = credentials
        return {
          url: USER_END_POINT.STRIPE_SUBSCRIPTION,
          method: 'PATCH',
          body: { oldPriceId, newPriceId, token },
          credentials: 'include',
          headers: {
            [HEADERS_TITLE.XSRF]: token
          }
        }
      }
    }),
    postChangePassword: builder.mutation<any, any>({
      query: ({ xsrf, body }) => {
        return {
          url: USER_END_POINT.CHANGE_PASSWORD,
          credentials: 'include',
          method: 'POST',
          headers: {
            [HEADERS_TITLE.XSRF]: xsrf
          },
          body
        }
      }
    }),
    postChangeEmail: builder.mutation<any, any>({
      query: ({ xsrf, body }) => {
        return {
          url: USER_END_POINT.CHANGE_EMAIL,
          credentials: 'include',
          method: 'POST',
          headers: {
            [HEADERS_TITLE.XSRF]: xsrf
          },
          body
        }
      }
    }),
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    postForgotPassword: builder.mutation<void, IForgotPasswordQuery>({
      query: ({
        email,
        xsrf
      }) => {
        return {
          url: USER_END_POINT.FORGOT_PASSWORD,
          credentials: 'include',
          method: 'POST',
          headers: {
            [HEADERS_TITLE.XSRF]: xsrf
          },
          body: { email }
        }
      }
    }),
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    postResetPassword: builder.mutation<void, any>({
      query: (credentials) => {
        const {
          email,
          password,
          passwordConfirmation,
          resetToken,
          xsrf
        } = credentials
        return {
          url: USER_END_POINT.RESET_PASSWORD,
          credentials: 'include',
          method: 'POST',
          headers: {
            [HEADERS_TITLE.XSRF]: xsrf
          },
          body: {
            email,
            password,
            passwordConfirmation,
            token: resetToken
          }
        }
      }
    }),
    getConfirmEmail: builder.mutation<any, any>({
      query: ({ token }) => {
        return {
          url: USER_END_POINT.CONFIRM_EMAIL(token),
          credentials: 'include',
          method: 'GET'
        }
      }
    }),
    getIHFCredentials: builder.mutation<IIHFCredentials, string>({
      query: (xsrf) => {
        return {
          url: USER_END_POINT.IHF_LOGIN,
          credentials: 'include',
          method: 'GET',
          headers: {
            [HEADERS_TITLE.XSRF]: xsrf
          }
        }
      }
    }),
    getLastInvoiceLink: builder.mutation<any, any>({
      query: () => {
        return {
          url: USER_END_POINT.STRIPE_INVOICES_LAST_URL,
          method: 'GET',
          credentials: 'include'
        }
      }
    }),
    getAnalytics: builder.mutation<any, any>({
      query: () => {
        return {
          url: STATISTICS.ANALYTICS,
          method: 'GET',
          credentials: 'include'
        }
      }
    })
  })
})

export const {
  useGetUserMutation,
  useGetProductsMutation,
  useGetPricesMutation,
  useGetSubscriptionMutation,
  usePostSubscriptionMutation,
  useDeleteSubscriptionMutation,
  usePatchSubscriptionMutation,
  usePostForgotPasswordMutation,
  usePostResetPasswordMutation,
  usePostChangePasswordMutation,
  usePostChangeEmailMutation,
  useGetConfirmEmailMutation,
  useGetIHFCredentialsMutation,
  useGetLastInvoiceLinkMutation,
  useGetAnalyticsMutation
} = userApi
